import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar, User, Eye, Share2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { BlogPostSchema } from "@/components/BlogPostSchema";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SafeHtmlContent } from "@/components/SafeHtmlContent";
import { preserveRichText } from "@/utils/textFormatter";
import { supabase } from "@/integrations/supabase/client";

interface Article {
  id: string;
  title: string;
  subtitle?: string;
  content: string;
  image_url?: string;
  author: string;
  category: string;
  meta_title?: string;
  meta_description?: string;
  keywords?: string;
  canonical_url?: string;
  slug: string;
  read_time: number;
  created_at: string;
  updated_at: string;
  is_published: boolean;
}

export const BlogPost = () => {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!id) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('articles')
          .select('*')
          .eq('slug', id)
          .eq('is_published', true)
          .single();

        if (error || !data) {
          setNotFound(true);
        } else {
          setArticle(data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'article:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  if (loading) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gradient-sky flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement de l'article...</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (notFound || !article) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gradient-sky flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-display font-bold mb-4">Article non trouvé</h1>
            <p className="text-muted-foreground mb-8">Cet article n'existe pas ou a été supprimé.</p>
            <Link to="/blog">
              <Button variant="hero">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au Blog
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO
        title={article.meta_title || article.title}
        description={article.meta_description || article.subtitle}
        keywords={article.keywords || ''}
        canonicalUrl={article.canonical_url || `https://chateaudeclementine.com/blog/${article.slug}`}
      />
      <BlogPostSchema
        title={article.title}
        description={article.subtitle || ''}
        author={article.author}
        datePublished={new Date(article.created_at).toISOString()}
        image={article.image_url || '/default-article-image.webp'}
        url={article.canonical_url || `https://chateaudeclementine.com/blog/${article.slug}`}
        category={article.category}
      />
      <Header />
      <main className="min-h-screen bg-gradient-sky">
        {/* Hero Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            {/* Back Link */}
            <Link to="/blog" className="inline-flex items-center text-primary hover:text-primary/80 transition-colors mb-8">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au Blog
            </Link>

            {/* Article Header */}
            <div className="text-center mb-12">
              <Badge className="mb-4 gradient-primary text-white border-0">{article.category}</Badge>
              <h1 className="text-3xl md:text-5xl font-display font-bold mb-6 leading-tight">
                {article.title}
              </h1>
              {article.subtitle && (
                <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                  {article.subtitle}
                </p>
              )}
              
              {/* Article Meta */}
              <div className="flex items-center justify-center text-sm text-muted-foreground space-x-6 mb-8">
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  <span>{article.author}</span>
                </div>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>{new Date(article.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="flex items-center">
                  <Eye className="w-4 h-4 mr-2" />
                  <span>{article.read_time} min</span>
                </div>
              </div>

              {/* Share Button */}
              <Button variant="outline" size="sm" className="mb-8">
                <Share2 className="w-4 h-4 mr-2" />
                Partager l'article
              </Button>
            </div>

            {/* Article Image */}
            <div className="mb-12">
              <div className="relative h-64 md:h-96 rounded-lg overflow-hidden shadow-card bg-gradient-subtle">
                <img
                  src={article.image_url || '/default-article-image.webp'}
                  alt={article.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = '/default-article-image.webp';
                  }}
                />
                {/* Overlay gradient for better text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <article className="bg-white rounded-2xl shadow-card p-8 md:p-12 mb-12">
              <SafeHtmlContent
                html={preserveRichText(article.content)}
                className="prose prose-lg prose-primary max-w-none 
                  prose-headings:font-display prose-headings:font-bold prose-headings:text-primary
                  prose-h1:text-3xl prose-h1:mt-0 prose-h1:mb-8
                  prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-b prose-h2:border-border prose-h2:pb-2
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4 prose-h3:text-secondary
                  prose-h4:text-lg prose-h4:mt-6 prose-h4:mb-3 prose-h4:text-primary prose-h4:font-semibold
                  prose-p:mb-6 prose-p:leading-relaxed prose-p:text-lg
                  prose-ul:mb-6 prose-ul:space-y-2 prose-ol:mb-6 prose-ol:space-y-2 
                  prose-li:mb-2 prose-li:leading-relaxed
                  prose-strong:text-primary prose-strong:font-semibold
                  prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-primary/5 prose-blockquote:rounded-r-lg prose-blockquote:p-4 prose-blockquote:my-6
                  prose-code:bg-muted prose-code:px-2 prose-code:py-1 prose-code:rounded
                  prose-a:text-primary prose-a:font-medium hover:prose-a:text-primary/80 prose-a:no-underline hover:prose-a:underline
                  prose-img:rounded-lg prose-img:shadow-card prose-img:my-8"
              />
            </article>

            {/* Call to Action */}
            <div className="mt-8 p-8 gradient-primary rounded-2xl text-white text-center shadow-elegant">
              <h3 className="text-2xl font-display font-bold mb-4">
                Prêt à Organiser une Fête Inoubliable ?
              </h3>
              <p className="mb-6 opacity-90 text-lg">
                Découvrez nos châteaux gonflables et réservez dès maintenant pour votre prochain événement !
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/chateaux">
                  <Button variant="hero" size="lg" className="w-full sm:w-auto">
                    Voir Nos Châteaux
                  </Button>
                </Link>
                <Link to="/#contact">
                  <Button variant="secondary" size="lg" className="w-full sm:w-auto bg-white/20 backdrop-blur-sm text-white border-2 border-white hover:bg-white hover:text-primary transition-all duration-300">
                    Nous Contacter
                  </Button>
                </Link>
              </div>
            </div>

            {/* Back to Blog */}
            <div className="text-center mt-12">
              <Link to="/blog">
                <Button variant="outline" size="lg" className="shadow-card">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voir Tous les Articles
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};